import { useState } from 'react';
import { Alert, Box, IconButton, Loader, Typography } from '@coolblue-development/freddy';
import { datadogLogs } from '@datadog/browser-logs';
import { Download, Refresh } from '@coolblue-development/icons';
import { fetchContact } from '../../../../../utils/fetchContact';
import { getChatSession } from '../../../../../utils/getChatSession';
import BaseAttachmentChip from '../../Email/BaseAttachmentChip';

interface ChatAttachmentChipsProps {
  attachments: connect.AttachmentItem[];
  contactId: string;
}

const ERROR_COLOR = '#D71F1F';

// Connect never says why it rejected a file, so neither line guesses at a cause.
export const REJECTED_FILE_MESSAGE = 'This file could not be delivered.';
export const REJECTED_FILES_BANNER = 'The customer sent a file that could not be delivered. Ask them to send it again.';

const ChatAttachmentChips = ({ attachments, contactId }: ChatAttachmentChipsProps): JSX.Element => {
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());
  const [failedIds, setFailedIds] = useState<Set<string>>(new Set());

  const handleDownload = async (attachmentId: string): Promise<void> => {
    setLoadingIds(prev => new Set(prev).add(attachmentId));
    try {
      const chatSession = await getChatSession(fetchContact(contactId));
      if (!chatSession) throw new Error('No chat session for contact');
      const url = await chatSession.getAttachmentURL({ attachmentId });
      window.open(url, '_blank', 'noopener,noreferrer');
      setFailedIds(prev => new Set([...prev].filter(id => id !== attachmentId)));
    } catch (error) {
      datadogLogs.logger.error('Failed to download attachment', { channel: 'chat' }, error as Error);
      setFailedIds(prev => new Set(prev).add(attachmentId));
    } finally {
      setLoadingIds(prev => new Set([...prev].filter(id => id !== attachmentId)));
    }
  };

  const hasRejected = attachments.some(({ Status }) => Status === 'REJECTED');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }} data-testid="chat-attachments">
      {attachments.map(({ AttachmentId, AttachmentName, Status }) => {
        if (Status === 'REJECTED') {
          return (
            <Box key={AttachmentId}>
              <BaseAttachmentChip fileName={AttachmentName} variant="error" dataTestId="chat-attachment-rejected" />
              <Typography fontSize="12px" color={ERROR_COLOR} sx={{ marginTop: '4px' }}>
                {REJECTED_FILE_MESSAGE}
              </Typography>
            </Box>
          );
        }

        const isFailed = failedIds.has(AttachmentId);
        return (
          <BaseAttachmentChip
            key={AttachmentId}
            fileName={AttachmentName}
            variant={isFailed ? 'error' : 'default'}
            dataTestId="chat-attachment"
            trailing={
              loadingIds.has(AttachmentId) ? (
                <Loader size="small" />
              ) : (
                <IconButton
                  size="small"
                  aria-label={`Download ${AttachmentName}`}
                  onClick={() => void handleDownload(AttachmentId)}
                  sx={{ padding: '4px' }}
                >
                  {isFailed ? <Refresh color={ERROR_COLOR} /> : <Download color="#0090E3" />}
                </IconButton>
              )
            }
          />
        );
      })}
      {hasRejected && <Alert description={REJECTED_FILES_BANNER} intent="error" />}
    </Box>
  );
};

export default ChatAttachmentChips;
